import type {SyntheticEvent} from 'react'
import React, {forwardRef, useMemo} from 'react'
import {Link, useLoaderData, useRouteLoaderData} from '@remix-run/react'
import clsx from 'clsx'
import {slugToText} from '@roundforest/text-transforms-commons'
import {AmazonLogoIcon} from '~/icons/amazon-logo-icon'
import type {AffiliateLink, OtherProduct, ProductStoreDataLoaderResponse, RootLoaderResponse} from '~/types'
import {getDomainConfig} from '~/domains'
import {getTranslation} from '~/localization/translation'
import {CLICK_ID_PLACEHOLDER} from '~/utils/affiliation'
import {useReportItemSeen} from '~/hooks/analytic-hooks'
import {getSchemaPriceValidUntil} from '~/utils/commons'
import DiscountCornerRibbon from '../badges/DiscountCornerRibbon'

interface RecommendedProductsCardProps {
  productInfo: OtherProduct
  index: number
  handleCardClick: (
    _e: SyntheticEvent,
    index: number,
    affiliateLink: AffiliateLink,
    affiliateLinkWithClickId: string,
    itemName: string,
    redirectUrl: string,
    elementName: string,
    elementPlace: string,
  ) => void
  shouldForceTrackAnalytics: boolean
  trackSearchParams: string
  clickId: string
}

const RecommendedProductsCard = forwardRef<HTMLDivElement, RecommendedProductsCardProps>(
  ({productInfo, index, handleCardClick, shouldForceTrackAnalytics, trackSearchParams, clickId}, forwardRef) => {
    const {language, currencyCode, domain, locale, hostname, renderTimestamp} = useRouteLoaderData(
      'root',
    ) as RootLoaderResponse
    const {slug} = useLoaderData() as ProductStoreDataLoaderResponse
    const {styles: stylesBydomain} = getDomainConfig(domain, locale)
    const translations = getTranslation(language)
    const {image, name, oldPrice, price, discount, affiliateLink, position, asin} = productInfo

    const currencyFormatter = useMemo(
      () =>
        new Intl.NumberFormat(language, {
          style: 'currency',
          currency: currencyCode,
          minimumFractionDigits: 0,
        }),
      [language, currencyCode],
    )

    const [cardRef] = useReportItemSeen({
      item_list_id: slug,
      item_name: name,
      item_source: 'list',
      partner_item_brand: name,
      partner_item_id: asin,
      affiliation: affiliateLink.type,
      is_dws: false,
      price,
      currency: currencyCode,
      click_element_name: 'recommended-products-card',
      index: index + 1,
    })

    const affiliateLinkWithClickId = affiliateLink?.url?.replaceAll(CLICK_ID_PLACEHOLDER, clickId)

    const redirectUrl = shouldForceTrackAnalytics
      ? `/redirect-analytics?affiliateLink=${encodeURIComponent(affiliateLinkWithClickId)}&${trackSearchParams}`
      : affiliateLinkWithClickId

    return (
      <>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org/',
              '@type': 'Product',
              name: name,
              image: [image],
              description: name,
              brand: {
                '@type': 'Brand',
                name: name,
              },
              offers: {
                '@type': 'Offer',
                url: `https://${hostname}${affiliateLinkWithClickId}`,
                priceCurrency: currencyCode,
                price: price,
                priceValidUntil: getSchemaPriceValidUntil(renderTimestamp, 'en-CA'),
                itemCondition: 'https://schema.org/NewCondition',
                availability: 'https://schema.org/InStock',
                seller: {
                  '@type': 'Organization',
                  name: name ?? 'Amazon',
                },
              },
            }),
          }}
        ></script>

        <div
          className="relative flex min-h-full w-60 min-w-60 flex-col rounded-xl border border-[#D5D9D9] bg-white"
          ref={forwardRef}
        >
          {discount && discount > 0 ? (
            <DiscountCornerRibbon className="absolute -left-1 -top-1 w-full" discount={discount} />
          ) : null}
          <AmazonLogoIcon className="absolute right-4 top-2 h-6 w-16 object-scale-down" />
          <div className="mt-7 flex h-full flex-col items-center justify-center gap-2 px-2 py-3" ref={cardRef}>
            <div className="relative flex size-32 items-center justify-center">
              <a
                className="absolute inset-0"
                href={redirectUrl}
                onClick={(e) =>
                  handleCardClick(
                    e,
                    index + 1,
                    affiliateLink,
                    affiliateLinkWithClickId,
                    name,
                    redirectUrl,
                    'image',
                    `recommended-products-card-${index + 1}`,
                  )
                }
                aria-label={`view ${slug}`}
                target="_blank"
                rel="sponsored noreferrer"
              ></a>
              <img src={image} alt={name} className="size-32 object-scale-down" loading="lazy" />
            </div>
            <span className="relative line-clamp-1 text-start text-base font-semibold">
              <a
                className="absolute inset-0"
                href={redirectUrl}
                onClick={(e) =>
                  handleCardClick(
                    e,
                    index + 1,
                    affiliateLink,
                    affiliateLinkWithClickId,
                    name,
                    redirectUrl,
                    'title',
                    `recommended-products-card-${index + 1}`,
                  )
                }
                aria-label={`view ${slug}`}
                target="_blank"
                rel="sponsored noreferrer"
              ></a>
              {name}
            </span>
            {slug ? (
              <Link
                to={`/${slug}`}
                prefetch="intent"
                aria-label={`view ${name}`}
                className="self-center rounded-lg bg-[#EFF5FF] px-1.5 py-0.5 text-sm font-light capitalize text-[#18297C] underline"
              >
                {translations.ProductPage.bestIn(position, slugToText(slug || ''))}
              </Link>
            ) : null}
          </div>
          <div className="border-t-dotted mt-auto flex h-fit w-full flex-col justify-center gap-1 border-t border-[#E3E3E3] p-3">
            <div className="relative mt-auto flex flex-row items-center justify-center gap-1">
              <a
                className="absolute inset-0"
                href={redirectUrl}
                onClick={(e) =>
                  handleCardClick(
                    e,
                    index + 1,
                    affiliateLink,
                    affiliateLinkWithClickId,
                    name,
                    redirectUrl,
                    'price',
                    `recommended-products-card-${index + 1}`,
                  )
                }
                aria-label={`view ${slug}`}
                target="_blank"
                rel="sponsored noreferrer"
              ></a>
              {price ? <span className="text-[22px] text-[#FE282E]">{currencyFormatter.format(price)}</span> : null}
              {oldPrice ? (
                <span className="text-[15px] font-light text-[#999999] line-through">
                  {currencyFormatter.format(oldPrice)}
                </span>
              ) : null}
            </div>
            <a
              href={redirectUrl}
              target="_blank"
              rel="sponsored noreferrer"
              onClick={(e) =>
                handleCardClick(
                  e,
                  index + 1,
                  affiliateLink,
                  affiliateLinkWithClickId,
                  name,
                  redirectUrl,
                  'cta',
                  `recommended-products-card-${index + 1}`,
                )
              }
              className={clsx(
                'w-full rounded-[50px] p-2 text-center text-[13px] font-normal uppercase',
                stylesBydomain.LISTPAGE_BUTTON_PRIMARY,
              )}
            >
              {translations.Home.viewDeal}
            </a>
          </div>
        </div>
      </>
    )
  },
)

export default RecommendedProductsCard
